import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchApi } from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userOrg, setUserOrg] = useState(null);

  // Initialize auth state from stored token
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      console.log('AuthProvider: Initializing with token:', token ? 'Token exists' : 'No token');
      
      if (token) {
        try {
          // Try to get user data with the stored token
          const data = await fetchApi('/api/auth/me', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (data && data.user) {
            console.log('AuthProvider: User data retrieved successfully');
            
            // Store user data with token
            const userData = {
              ...data.user,
              token // Ensure token is included in the user object
            };
            
            console.log('AuthProvider: Setting user with token', userData);
            setUser(userData);
            
            // Fetch organization data for the user
            if (data.user.organization_id) {
              try {
                const orgData = await fetchApi(`/api/organizations/${data.user.organization_id}`, {
                  headers: {
                    'Authorization': `Bearer ${token}`
                  }
                });
                
                if (orgData && orgData.success && orgData.organization) {
                  setUserOrg(orgData.organization);
                }
              } catch (orgError) {
                console.error("Error fetching organization data:", orgError);
              }
            }
          } else {
            console.error("Invalid user data format during initialization:", data);
            setUser({ token });
          }
        } catch (error) {
          console.error("Auth initialization error:", error);
          localStorage.removeItem('token');
          setUser(null);
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (token) => {
    if (!token) {
      console.error("No token provided to login function");
      return;
    }
    
    console.log('AuthProvider: Login with token');
    
    // Store token in localStorage
    localStorage.setItem('token', token);
    
    try {
      // Fetch user data with the token
      const data = await fetchApi('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (data && data.user) {
        // Set user data with token
        const userData = {
          ...data.user,
          token // Ensure token is included in the user object
        };
        
        console.log('AuthProvider: Setting user after login', userData);
        setUser(userData);
        
        // Fetch organization data if organization_id is available
        if (data.user.organization_id) {
          try {
            const orgData = await fetchApi(`/api/organizations/${data.user.organization_id}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (orgData && orgData.success && orgData.organization) {
              setUserOrg(orgData.organization);
            }
          } catch (orgError) {
            console.error("Error fetching organization data:", orgError);
          }
        }
      } else {
        console.error("Invalid user data format after login:", data);
        // Fallback to minimal user with token
        setUser({ token });
      }
    } catch (error) {
      console.error("Error fetching user data after login:", error);
      // Fallback to minimal user with token
      setUser({ token });
    }
  };

  // Logout function
  const logout = () => {
    console.log('AuthProvider: Logging out user');
    localStorage.removeItem('token');
    setUser(null);
    setUserOrg(null);
  };

  // Debug current user state
  useEffect(() => {
    console.log('AuthProvider: Current user state:', user ? { ...user, token: user.token ? 'exists' : 'missing' } : 'no user');
  }, [user]);

  // Provide auth context
  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        userOrg, 
        loading, 
        login, 
        logout, 
        isAdmin: user && (user.role === 'admin' || user.is_admin === true),
        token: user?.token
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 